import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Grid,
  Checkbox,
  FormControlLabel,
  Card,
  CardContent,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Snackbar,
  Alert,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../config";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const baseUrl = config.baseURL;
const PRIMARY_COLOR = "#2E4053";
const ACCENT_COLOR = "#2E4053";
const steps = ["Module Info", "Add Fields", "Permissions"];
const permissionTypes = ["view", "add", "edit", "delete"];

const ModulePermission = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [permissionsData, setPermissionsData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditingExisting, setIsEditingExisting] = useState(false);
  const [moduleName, setModuleName] = useState("");
  const [showAdminNotice, setShowAdminNotice] = useState(false);
  const [userRes, setUserRes] = useState([]);

  const token = localStorage.getItem("token");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [rolesRes, permRes, userRes] = await Promise.all([
          axios.get(`${baseUrl}/api/role-nomaster`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${baseUrl}/api/module-role-permissions/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${baseUrl}/api/usersProfile`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        const rolesData = rolesRes.data.data || [];
        const UserData = userRes.data.data || [];
        console.log("UserData", UserData);
        const fetchedPermissions = permRes.data?.permissions || [];

        const perms = {};
        let hasExistingRecords = false;

        fetchedPermissions.forEach((perm) => {
          perms[perm.role_id] = {
            id: perm.id,
            permissions: perm.permissions || {},
          };
          if (perm.id) hasExistingRecords = true;
        });

        // ✅ Admin is auto-assigned by backend → always show notice
        setShowAdminNotice(true);
        console.log("rolesRes", rolesRes);
        // ✅ Module name from backend
        setModuleName(fetchedPermissions[0]?.module_name || "");
        setUserRes(UserData);
        // ✅ Editing state
        setPermissionsData(perms);
        setIsEditingExisting(hasExistingRecords);

        setRoles(rolesData);
        setPermissionsData(perms);
      } catch (err) {
        console.error("Error fetching module role permissions:", err);
        toast.error("Failed to load roles or permissions.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, token]);

  const handleCheckboxChange = (roleId, permType) => {
    setPermissionsData((prev) => ({
      ...prev,
      [roleId]: {
        ...prev[roleId],
        permissions: {
          ...prev[roleId]?.permissions,
          [permType]: !prev[roleId]?.permissions?.[permType],
        },
      },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await Promise.all(
        roles.map(async (role) => {
          const roleData = permissionsData[role.id];
          const payload = userRes.map((user) => ({
            module_id: module,
            role_id: user.role_id, // or create user-specific role_id if null
            user_id: user.id, // if you add a user_id column
            permissions: permissionsData[user.role_id || user.id],
          }));

          if (roleData?.id) {
            await axios.put(
              `${baseUrl}/api/module-role-permissions/${roleData.id}`,
              payload,
              { headers: { Authorization: `Bearer ${token}` } }
            );
          } else {
            await axios.post(
              `${baseUrl}/api/module-role-permissions`,
              payload,
              {
                headers: { Authorization: `Bearer ${token}` },
              }
            );
          }
        })
      );

      const action = isEditingExisting ? "updated" : "created";

      toast.success(`Permissions successfully ${action} `, {
        position: "top-right",
        autoClose: 2000,
        onClose: () => navigate(`/dashboard/${moduleName}`),
      });

      // Clear the admin notice after save
      // setShowAdminNotice(false);
    } catch (err) {
      console.error(err);
      toast.error("Failed to save permissions ");
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: "#F4F6F8",
        }}
      >
        <CircularProgress sx={{ color: PRIMARY_COLOR }} />
      </Box>
    );

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 4 },
        minHeight: "100vh",
        backgroundColor: "#F4F6F8",
      }}
    >
      <Typography
        variant="h4"
        sx={{ fontWeight: 600, mb: 4, color: PRIMARY_COLOR }}
      >
        🛡️ Module Permissions
      </Typography>

      <Stepper activeStep={2} sx={{ mb: 5 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <ToastContainer />
      {/* Empty state */}
      {roles.length === 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          No users found. Please add users if you want to assign permissions to
          them.
        </Alert>
      )}

      {/* Show Admin Notice */}
      {showAdminNotice && (
        <Alert severity="info" sx={{ mb: 3 }}>
          This module will be assigned to you by default as admin.
        </Alert>
      )}

      {userRes.map((user) => (
        <Card key={user.id} sx={{ mb: 3 }}>
          <Box sx={{ backgroundColor: "#F8FAFB", px: 3, py: 2 }}>
            <Typography sx={{ fontWeight: 600, color: PRIMARY_COLOR }}>
              {user.firstName} {user.lastName} ({user.role_name || user.role})
            </Typography>
          </Box>

          <CardContent>
            <Grid container spacing={2}>
              {permissionTypes.map((permType) => (
                <Grid item xs={6} md={3} key={permType}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={
                          permissionsData[user.role_id]?.permissions?.[
                            permType
                          ] || false
                        }
                        onChange={() =>
                          handleCheckboxChange(
                            user.role_id || user.id,
                            permType
                          )
                        }
                      />
                    }
                    label={permType.charAt(0).toUpperCase() + permType.slice(1)}
                  />
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      ))}

      <Box sx={{ mt: 5, display: "flex", gap: 2 }}>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          sx={{ backgroundColor: ACCENT_COLOR }}
        >
          {saving ? (
            <CircularProgress size={24} sx={{ color: "#FFF" }} />
          ) : isEditingExisting ? (
            "Update Permissions"
          ) : (
            "Save Permissions"
          )}
        </Button>
        <Button
          variant="outlined"
          onClick={() => navigate(`/dashboard/modules/fields/${id}`)}
        >
          ← Back
        </Button>
      </Box>
    </Box>
  );
};

export default ModulePermission;
